/**
 * Mapper de colores para los diferentes status de unidades
 * Usa clases de Tailwind CSS para colores fijos independientes del tema
 */

import type * as React from "react"

export type UnitStatus =
  | "START_LIST"
  | "GETTING_READY"
  | "RUNNING"
  | "UNOFFICIAL"
  | "OFFICIAL"
  | "PROTESTED"
  | "PROGRAMED_PAUSE"
  | "FINISHED"
  | "UNSCHEDULED"
  | "SCHEDULED"

export type StatusColors = {
  background: string
  border: string
  text: string
}

export const statusColorsMapper: Record<UnitStatus, StatusColors> = {
  START_LIST: {
    background: "!bg-yellow-500",
    border: "!border-yellow-600",
    text: "!text-white",
  },
  GETTING_READY: {
    background: "!bg-orange-500",
    border: "!border-orange-600",
    text: "!text-white",
  },
  RUNNING: {
    background: "!bg-green-500",
    border: "!border-green-600",
    text: "!text-white",
  },
  UNOFFICIAL: {
    background: "!bg-slate-500",
    border: "!border-slate-600",
    text: "!text-white",
  },
  OFFICIAL: {
    background: "!bg-blue-500",
    border: "!border-blue-600",
    text: "!text-white",
  },
  PROTESTED: {
    background: "!bg-red-500",
    border: "!border-red-600",
    text: "!text-white",
  },
  PROGRAMED_PAUSE: {
    background: "!bg-purple-500",
    border: "!border-purple-600",
    text: "!text-white",
  },
  FINISHED: {
    background: "!bg-indigo-500",
    border: "!border-indigo-600",
    text: "!text-white",
  },
  UNSCHEDULED: {
    background: "!bg-gray-200",
    border: "!border-gray-300",
    text: "!text-gray-800",
  },
  SCHEDULED: {
    background: "!bg-gray-200",
    border: "!border-gray-300",
    text: "!text-gray-800",
  },
}

/**
 * Obtiene los colores para un status específico
 * @param status - El status de la unidad
 * @returns Las clases CSS para el status o colores por defecto
 */
export function getStatusColors(status?: UnitStatus | string): StatusColors {
  if (!status || !(status in statusColorsMapper)) {
    return {
      background: "!bg-gray-200",
      border: "!border-gray-300",
      text: "!text-gray-800",
    }
  }

  return statusColorsMapper[status as UnitStatus]
}

/**
 * Obtiene estilos inline para un status específico (máxima especificidad CSS)
 * @param status - El status de la unidad
 * @returns Objeto de estilos CSS inline
 */
export function getInlineStylesForStatus(status?: UnitStatus | string): React.CSSProperties {
  const colorMap: Record<UnitStatus, React.CSSProperties> = {
    START_LIST: {
      backgroundColor: "#eab308", // yellow-500
      borderColor: "#ca8a04", // yellow-600
      color: "white",
    },
    GETTING_READY: {
      backgroundColor: "#f97316", // orange-500
      borderColor: "#ea580c", // orange-600
      color: "white",
    },
    RUNNING: {
      backgroundColor: "#22c55e", // green-500
      borderColor: "#16a34a", // green-600
      color: "white",
    },
    UNOFFICIAL: {
      backgroundColor: "#64748b", // slate-500
      borderColor: "#475569", // slate-600
      color: "white",
    },
    OFFICIAL: {
      backgroundColor: "#3b82f6", // blue-500
      borderColor: "#2563eb", // blue-600
      color: "white",
    },
    PROTESTED: {
      backgroundColor: "#ef4444", // red-500
      borderColor: "#dc2626", // red-600
      color: "white",
    },
    PROGRAMED_PAUSE: {
      backgroundColor: "#a855f7", // purple-500
      borderColor: "#9333ea", // purple-600
      color: "white",
    },
    FINISHED: {
      backgroundColor: "#6366f1", // indigo-500
      borderColor: "#4f46e5", // indigo-600
      color: "white",
    },
    UNSCHEDULED: {
      backgroundColor: "#f1f5f9", // gray-100
      borderColor: "#d1d5db", // gray-300
      color: "#374151", // gray-700
    },
    SCHEDULED: {
      backgroundColor: "#f1f5f9", // gray-100
      borderColor: "#d1d5db", // gray-300
      color: "#374151", // gray-700
    },
  }

  if (!status || !(status in colorMap)) {
    return {
      backgroundColor: "#f1f5f9", // gray-100
      borderColor: "#d1d5db", // gray-300
      color: "#374151", // gray-700
    }
  }

  return colorMap[status as UnitStatus]
}
